import type { DocumentListRecord, DocumentPermissionAction } from './documentCRUD';

export * from './authority';
export * from './authorityProjection';

export interface User {
  id: string;
  name: string;
  email: string;
  role: UserRole; // Runtime account role (legacy values are normalized at data boundaries)
  /** Canonical Gate R authority. Legacy role fields are display/migration evidence only. */
  account_role?: import('./authority').MvpAccountRole | null;
  /** AX1 control-plane role. Customer users never receive this field. */
  platform_role?: PlatformRole | null;
  /**
   * Non-authorizing platform specialization tags (e.g. 'security', 'commercial').
   * Used for routing / staffing / filtering / display ONLY. Never an authority
   * input. Authority for platform staff comes from `account_role` alone.
   */
  platform_functions?: import('./authority').PlatformFunction[];
  /**
   * Explicit lifecycle-managed platform capability grants (differentiated
   * control-plane powers beyond the reviewed base role policy). Authoritative
   * ONLY through the platform capability resolver; in production these come
   * from a server-managed table. Never derived from `platform_functions`.
   */
  platform_capability_assignments?: import('./authority').PlatformCapabilityAssignment[];
  /** Customer workspace role. Platform accounts do not require workspace membership. */
  workspace_role?: WorkspaceRole | LegacyWorkspaceRole | PlatformRole;
  workspace_id?: string; // Workspace ID for multi-tenant support
  employmentType?: 'internal' | 'contractor' | 'external';
  // Note: Hybrid roles (roles?: UserRole[]) have been removed. Use single 'role' field only.
  avatar?: string;
  department?: string;
  company?: string;
  phone?: string;
  timezone?: string; // IANA timezone (e.g., 'America/New_York')
  country?: string; // Country of residence (ISO 3166-1 alpha-2 or full name)
  joinDate: Date;
  lastActive: Date;
  status?: 'active' | 'inactive' | 'pending' | 'suspended';
  permissions?: string[];
  /**
   * Executable scoped-relationship records for this user (project_manager,
   * vendor_representative, partner_representative, investor_representative, etc).
   * These are NOT account roles. Normal resource visibility (e.g. project lists)
   * must be derived from these records via `lib/auth/scopedRelationshipResolver.ts`,
   * never from `role`/`account_role` alone (except for organization-admin overrides).
   */
  scopedRelationships?: import('./authority').UserScopedRelationship[];
}

/** AX1-owned control-plane accounts. */
export type PlatformRole =
  | 'platform_owner'
  /**
   * Transitional compatibility role. Do not assign to new accounts; use one
   * of the named operational functions below.
   */
  | 'platform_admin'
  | 'system_admin'
  | 'security_admin'
  | 'support_admin'
  | 'commercial_admin'
  | 'implementation_admin';

/** Delegated administrative functions inside a customer organisation. */
export type CustomerAdminRole =
  | 'organization_admin'
  | 'customer_security_admin'
  | 'business_admin'
  | 'workspace_admin';

/**
 * @deprecated CustomerParticipationRole is NOT an account or workspace role.
 * These are scoped relationships defined in authority.ts:
 * - project_manager (scoped to project)
 * - partner_representative (scoped to company/project/workspace)
 * - vendor_representative (scoped to company/project/workspace)
 * - investor_representative (scoped to investment)
 * 
 * Kept only for legacy migration compatibility. Do not use in new authorization logic.
 */
export type CustomerParticipationRole =
  | 'project_manager'
  | 'partner'
  | 'vendor'
  | 'investor';

/** Fixed roles available inside a licensed customer workspace. */
export type WorkspaceRole = CustomerAdminRole;

/** Values accepted only while reading older AX1 records. Never offer these in UI. */
export type LegacyUserRole = 'admin' | 'member' | 'team_member' | 'business_dev_manager';
export type LegacyWorkspaceRole = 'admin' | 'owner' | 'member';

export type CanonicalAccountRole = PlatformRole | WorkspaceRole;
export type UserRole = CanonicalAccountRole | LegacyUserRole;

export const PLATFORM_ACCOUNT_ROLES: readonly PlatformRole[] = [
  'platform_owner',
  'platform_admin',
  'system_admin',
  'security_admin',
  'support_admin',
  'commercial_admin',
  'implementation_admin',
] as const;

/** Roles offered for new AX1 internal assignments. */
export const PLATFORM_FUNCTION_ROLES: readonly Exclude<PlatformRole, 'platform_admin'>[] = [
  'platform_owner',
  'system_admin',
  'security_admin',
  'support_admin',
  'commercial_admin',
  'implementation_admin',
] as const;

export const CUSTOMER_WORKSPACE_ROLES: readonly WorkspaceRole[] = [
  'organization_admin',
  'customer_security_admin',
  'business_admin',
  'workspace_admin',
] as const;

export const CUSTOMER_ADMIN_ROLES: readonly CustomerAdminRole[] = [
  'organization_admin',
  'customer_security_admin',
  'business_admin',
  'workspace_admin',
] as const;

/**
 * @deprecated Do not use for authorization.
 * These are scoped relationships, not workspace roles.
 * Kept only for migration compatibility.
 */
export const CUSTOMER_PARTICIPATION_ROLES: readonly CustomerParticipationRole[] = [
  'project_manager',
  'partner',
  'vendor',
  'investor',
] as const;

// Human-readable labels for display only (account roles only)
export const USER_ROLE_LABELS: Record<UserRole, string> = {
  platform_owner: 'Platform Owner',
  platform_admin: 'Platform Admin (transitional)',
  system_admin: 'System Admin',
  security_admin: 'Security Admin',
  support_admin: 'Support Admin',
  commercial_admin: 'Commercial Admin',
  implementation_admin: 'Implementation Admin',
  organization_admin: 'Workspace Admin',
  customer_security_admin: 'Customer Security Admin',
  business_admin: 'Workspace Admin',
  workspace_admin: 'Workspace Admin',
  // Compatibility labels are never included in role selectors.
  admin: 'Platform Admin',
  member: 'Member',
  team_member: 'Team Member',
  business_dev_manager: 'Business Dev Manager',
};

export type EmploymentType =
  | 'internal'    // Full employees with company benefits and internal access
  | 'contractor'  // Temporary workers, project-based, limited access
  | 'external';   // Client users, partners, vendors - outside organization

export interface Project {
  id: string;
  name: string;
  description: string;
  type: ProjectType;
  status: ProjectStatus;
  priority: Priority;
  startDate: Date;
  endDate?: Date;
  progress: number;
  budget: number;
  spent: number;
  /**
   * @deprecated Use primaryClientCompany from project_companies instead.
   * This field is kept for backwards compatibility during migration.
   * @see CompanyService.getPrimaryClientCompany()
   */
  clientId?: string;
  /** 
   * Derived from project_companies join - the primary client company 
   * @see useProjectCompanies hook
   */
  primaryClientCompany?: {
    id: string;
    name: string;
  };
  client?: string;
  managerId?: string;
  teamMembers: Array<string | User>;
  milestones: Milestone[];
  documents: Document[];
  healthScore?: number;
  health?: 'excellent' | 'good' | 'fair' | 'at_risk';
  timeProgress?: number;
  budgetProgress?: number;
  scopeProgress?: number;
  lastUpdated?: Date;
  riskLevel?: 'low' | 'medium' | 'high';
  riskFactors?: string[];
  estimatedHours?: number;
  tasksCompleted?: number;
  totalTasks?: number;
  department?: string;
  website?: string;
  activities?: Array<{ user: string; time: string; content: string; icon?: React.ReactNode }>;
  blockers?: string[];
  /** Previous status before archive/trash — used by Restore to return to correct state */
  status_previous?: ProjectStatus | null;
  /** Timestamp of the last status lifecycle change (archive / trash / restore) */
  status_changed_at?: Date | null;
  /** User ID who performed the last status lifecycle change (for production audits) */
  status_changed_by?: string | null;
  /** Display name of user who performed the last lifecycle change (for UI display) */
  status_changed_by_name?: string | null;
  createdAt: Date;
  updatedAt: Date;
  /** Optional project capability; false unless explicitly enabled by an authorised manager. */
  dueDiligenceEnabled?: boolean;
}

export type ProjectType =
  | 'consulting'
  | 'go_to_market'
  | 'capital_raising'
  | 'marketing'
  | 'sales'
  | 'business_development'
  | 'market_intelligence'
  | 'manufacturing'
  | 'co_manufacturing'
  | 'recruitment'
  | 'development'
  | 'legal'
  | 'design';

export type ProjectStatus = 'planning' | 'active' | 'on_hold' | 'completed' | 'cancelled' | 'archived' | 'trashed';
export type Priority = 'low' | 'medium' | 'high' | 'critical';

export type TaskVisibility = 'internal' | 'client' | 'partner' | 'private' | 'custom';

export type MeasurementMode = 'time' | 'quantity';

export type VerificationStatus = 'pending' | 'approved' | 'rejected';

/** How a capital-relevant task contributes to a milestone Gate. */
export type TaskGovernanceRole = 'required' | 'weighted' | 'supporting' | 'blocker';

export interface Task {
  id: string;
  title: string;
  description: string;
  status: TaskStatus;
  priority: Priority;
  assigneeId: string;
  createdBy?: string; // User ID of who created the task
  assignedBy?: string; // User ID of who assigned the task (for tracking PM assignments)
  projectId: string;
  milestoneId?: string;
  estimatedHours: number;
  time_estimate?: number; // Optional legacy / alternate estimate field referenced in UI
  dueDate: Date;
  tags: string[];
  attachments: string[];
  comments: Comment[];
  notes?: string; // Personal notes/annotations for the task
  visibility?: TaskVisibility;
  visibleToUserIds?: string[]; // Used when visibility is 'custom'
  createdAt: Date;
  updatedAt: Date;
  sortOrder?: number; // Used for kanban reordering

  // Measurement mode: "time" (default) or "quantity"
  measurement_mode?: MeasurementMode;

  // Quantity-based fields (used when measurement_mode = 'quantity')
  unit?: string;                    // e.g. 'm2', 'm3', 'pcs', 'sets', 'deliverable'
  quantity_planned?: number;         // Required when measurement_mode = 'quantity'
  quantity_completed?: number;       // Cannot be negative
  quantity_verified?: number;        // Cannot exceed quantity_completed
  capital_relevant?: boolean;        // Default false
  verification_status?: VerificationStatus;
  /** Capital Gate contribution. Ignored when capital_relevant is false. */
  governance_role?: TaskGovernanceRole;
  /** Relative contribution among weighted Gate tasks. */
  governance_weight?: number;

  // Verification attribution
  verified_by?: string;              // User ID who actually approved/rejected
  verified_at?: string;              // ISO timestamp of last verification action
  validator_id?: string;             // Pre-assigned expected validator (optional)
}

export type TaskStatus = 'todo' | 'in_progress' | 'review' | 'completed';

export type TaskHistoryAction =
  | 'created'
  | 'updated'
  | 'status_changed'
  | 'assigned'
  | 'reassigned'
  | 'priority_changed'
  | 'due_date_changed'
  | 'description_updated'
  | 'milestone_changed'
  | 'comment_added'
  | 'attachment_added'
  | 'attachment_removed'
  | 'verification_changed'
  | 'deleted';

export interface TaskHistoryEntry {
  id: string;
  taskId: string;
  action: TaskHistoryAction;
  userId: string; // Who made the change
  userName?: string; // Cached user name for display
  userRole?: string; // Cached user role for context
  timestamp: Date;
  changes?: {
    field: string;
    oldValue: string | number | Date | boolean | null;
    newValue: string | number | Date | boolean | null;
  }[];
  description: string; // Human-readable description of the change
  metadata?: Record<string, string | number | boolean>; // Additional context data
}

export interface Milestone {
  id: string;
  name: string;
  description: string;
  projectId: string;
  startDate?: Date;
  dueDate: Date;
  status: MilestoneStatus;
  progress: number;
  tasks: Task[];
  deliverables: string[];
  assignedUsers?: string[];
  stageStatus?: MilestoneStageStatus;
  createdAt: Date;
  updatedAt?: Date;
}

export type MilestoneStatus = 'upcoming' | 'active' | 'completed' | 'overdue';
export type MilestoneStageStatus = 'Not Started' | 'In Progress' | 'In Review' | 'Blocked' | 'Complete';

export interface MilestoneDeliverable {
  id: string;
  type: 'File' | 'Link' | 'Prototype';
  title: string;
  url?: string;
  fileId?: string;
  description?: string;
  icon?: string;
  position: number;
  createdAt: Date;
  updatedAt?: Date;
}

export interface MilestoneOwnerInfo {
  id: string;
  name: string;
  avatar?: string;
  openTasks: {
    id: string;
    title: string;
    dueDate: Date;
  }[];
}

export interface Document {
  id: string;
  name: string;
  type: string;
  size: number;
  url: string;
  projectId: string;
  uploadedBy: string;
  permissions: DocumentPermission[];
  tags: string[];
  version: number;
  folder?: string;
  createdAt: Date;
  updatedAt: Date;
  /** Soft-delete timestamp — present if the document has been deleted */
  deletedAt?: Date | null;
  /** User ID who deleted this document */
  deletedBy?: string | null;
  /** Display name of the user who deleted this document */
  deletedByName?: string | null;
  /** Lifecycle status — e.g. 'draft' | 'review' | 'approved' | 'archived' */
  status?: string;
}

/**
 * Canonical UI-facing document shape for Documents subsystem.
 */
export type CanonicalDocument = DocumentListRecord;

export interface DocumentPermission {
  userId: string;
  permission: Extract<DocumentPermissionAction, 'view' | 'edit' | 'download'>;
}

interface Comment {
  id: string;
  taskId?: string;
  content: string;
  authorId: string;
  userId?: string;
  createdAt: Date;
  isInternal?: boolean;
  mentions: string[];
}

export interface Notification {
  id: string;
  type: NotificationType;
  title: string;
  message: string;
  userId: string;
  read: boolean;
  actionUrl?: string;
  createdAt: Date;
}

type NotificationType = 'task_assigned' | 'deadline_approaching' | 'milestone_completed' | 'document_shared' | 'project_update';

export interface DashboardStats {
  totalProjects: number;
  activeProjects: number;
  completedTasks: number;
  pendingTasks: number;
  upcomingDeadlines: number;
  teamUtilization: number;
  revenue: number;
  expensesByMonth: Array<{ month: string; amount: number }>;
}

export interface Client {
  id: string;
  name: string;
  email: string;
  company: string;
  phone?: string;
  address?: string;
  healthScore: number;
  projects: string[];
  contacts: Contact[];
  documents: string[];
  createdAt: Date;
}

export interface Partner {
  id: string;
  name: string;
  company: string;
  email: string;
  role: string;
  performance: number;
  projects: string[];
  documents: string[];
  createdAt: Date;
}

interface Contact {
  id: string;
  name: string;
  email: string;
  role: string;
  phone?: string;
}

export interface Message {
  id: string;
  content: string;
  senderId: string;
  recipientId?: string;
  channelId?: string;
  type: 'direct' | 'channel';
  attachments?: string[];
  mentions?: string[];
  linkedTasks?: string[];
  createdAt: Date;
}

export interface Channel {
  id: string;
  name: string;
  description?: string;
  projectId?: string;
  members: string[];
  type: 'project' | 'general' | 'private';
  createdAt: Date;
}

export interface CalendarEvent {
  id: string;
  title: string;
  description?: string;
  type: 'task' | 'milestone' | 'meeting';
  startDate: Date;
  endDate: Date;
  attendees?: string[];
  projectId?: string;
  taskId?: string;
  milestoneId?: string;
}

export interface SystemSettings {
  branding: {
    logo?: string;
    primaryColor: string;
    secondaryColor: string;
    companyName: string;
  };
  notifications: {
    emailEnabled: boolean;
    pushEnabled: boolean;
    deadlineReminder: number; // days before
  };
  security: {
    passwordPolicy: string;
    sessionTimeout: number;
    twoFactorRequired: boolean;
  };
}

export interface Role {
  id: string;
  name: string;
  description: string;
  permissions: string[];
  isCustom: boolean;
  createdAt: Date;
}

// ============================================================================
// COMPANY MODEL EXPORTS (Phase 1 of 2026 Access Model)
// ============================================================================
export * from './company';

// ============================================================================
// ACCESS GRANT EXPORTS (Phase 2 of 2026 Access Model)
// ============================================================================
export * from './accessGrant';

// ============================================================================
// PROJECT COMPANY EXPORTS (Phase 3 of 2026 Access Model)
// ============================================================================
export * from './projectCompany';

// ============================================================================
// CAPITAL RAISING EXPORTS (Phase 4 of 2026 Access Model)
// ============================================================================
export * from './capitalRaising';

// ============================================================================
// CANONICAL INVESTMENT, ROUND, POSITION AND RIGHTS MODEL
// ============================================================================
export * from './investment';

// ============================================================================
// DATA CLASSIFICATION EXPORTS (Phase 5 of 2026 Access Model)
// ============================================================================
export * from './dataClassification';

// ============================================================================
// PERSONA PRESET EXPORTS (Phase 6 of 2026 Access Model)
// ============================================================================
export * from './personaPreset';

// ============================================================================
// BENEFICIARY REGISTRY EXPORTS (Capital Execution)
// ============================================================================
export * from './beneficiary';

// ============================================================================
// RESOLVE AMBIGUOUS RE-EXPORTS (TS2308)
// When multiple modules export the same name via `export *`, TypeScript
// drops the name unless an explicit re-export disambiguates.
// ============================================================================
export { type DataClassification } from './dataClassification';
export { type DisclosureLevel, DISCLOSURE_LEVELS } from './dataClassification';
export { type FeatureFlag } from './personaPreset';
